<template>
    <card :title="pageTitle">
        <div>
            <!-- Search Section (only show if not viewing by compound) -->
            <div v-if="!compoundId">
                <b-row class="mb-4">
                    <b-col cols="6">
                        <fg-input v-model="searchTerm" label="Batch Number or Lot Number" placeholder="Enter batch or lot number" @keyup.enter="searchBatch" />
                    </b-col>
                    <b-col cols="2" class="pt-4">
                        <b-button variant="primary" @click="searchBatch" :disabled="!searchTerm">
                            <i class="fas fa-search" /> Search
                        </b-button>
                    </b-col>
                </b-row>

                <!-- Printer Selection -->
                <b-card v-if="!isLoadingPrinters && searched && batches.length > 0" class="mb-4">
                    <b-row>
                        <b-col cols="12">
                            <b-alert :show="!hasSupportedClientService" variant="warning" class="mb-2">
                                <i class="fa fa-warning"></i>
                                Scriptly Client Service not detected. Label printing will be unavailable.
                            </b-alert>
                            <label>Label Printer:</label>
                            <b-dropdown :text="prettyPrinterName(activeLocalPrinter)" variant="outline-primary" block>
                                <b-dropdown-item-button v-for="p in localPrinters" :key="p.name"
                                    :active="p.name === activeLocalPrinter?.name" @click="activeLocalPrinter = p">
                                    <i class="fa fa-print"></i>
                                    {{ prettyPrinterName(p) }}
                                    <div class="d-flex" style="gap: 6px;">
                                        <small v-if="p.default">Local Default</small>
                                        <small v-if="p.status !== 'Unknown'">{{ p.status }}</small>
                                    </div>
                                </b-dropdown-item-button>
                                <b-dropdown-item disabled v-if="!hasLocalPrinters">
                                    <i class="fa fa-info-circle" />
                                    To print to local printers, install<br>the Scriptly Client Service
                                </b-dropdown-item>
                            </b-dropdown>
                        </b-col>
                    </b-row>
                </b-card>
            </div>

            <!-- Compound Info Header (when viewing by compound) -->
            <div v-if="compoundId && compound">
                <b-card class="mb-4">
                    <h4>{{ compound.name }}</h4>
                    <p class="mb-0"><strong>Total Batches:</strong> {{ filteredBatches.length }} of {{ batches.length }}</p>
                </b-card>

                <!-- Printer Selection -->
                <b-card v-if="!isLoadingPrinters" class="mb-4">
                    <b-row>
                        <b-col cols="12">
                            <b-alert :show="!hasSupportedClientService" variant="warning" class="mb-2">
                                <i class="fa fa-warning"></i>
                                Scriptly Client Service not detected. Label printing will be unavailable.
                            </b-alert>
                            <label>Label Printer:</label>
                            <b-dropdown :text="prettyPrinterName(activeLocalPrinter)" variant="outline-primary" block>
                                <b-dropdown-item-button v-for="p in localPrinters" :key="p.name"
                                    :active="p.name === activeLocalPrinter?.name" @click="activeLocalPrinter = p">
                                    <i class="fa fa-print"></i>
                                    {{ prettyPrinterName(p) }}
                                    <div class="d-flex" style="gap: 6px;">
                                        <small v-if="p.default">Local Default</small>
                                        <small v-if="p.status !== 'Unknown'">{{ p.status }}</small>
                                    </div>
                                </b-dropdown-item-button>
                                <b-dropdown-item disabled v-if="!hasLocalPrinters">
                                    <i class="fa fa-info-circle" />
                                    To print to local printers, install<br>the Scriptly Client Service
                                </b-dropdown-item>
                            </b-dropdown>
                        </b-col>
                    </b-row>
                </b-card>

                <!-- Filters -->
                <b-card class="mb-4">
                    <b-card-title>Filters</b-card-title>
                    <b-row>
                        <b-col cols="3">
                            <label>Status</label>
                            <b-form-select v-model="statusFilter" :options="statusOptions" />
                        </b-col>
                        <b-col cols="4">
                            <label>Start Date</label>
                            <fg-input v-model="startDate" type="date" />
                        </b-col>
                        <b-col cols="4">
                            <label>End Date</label>
                            <fg-input v-model="endDate" type="date" />
                        </b-col>
                        <b-col cols="1" class="pt-4">
                            <b-button variant="outline-secondary" size="sm" @click="resetFilters">
                                <i class="fas fa-undo" /> Reset
                            </b-button>
                        </b-col>
                    </b-row>
                </b-card>
            </div>

            <!-- Batch List (multiple batches when viewing by compound) -->
            <div v-if="filteredBatches.length > 0">
                <div v-for="batch in filteredBatches" :key="batch.id" class="mb-4">
                    <b-card>
                        <!-- Batch Header -->
                        <b-card-title>
                            <b-row>
                                <b-col>
                                    <h5>
                                        Batch: {{ batch.batchNumber }}
                                        <b-badge :variant="getBatchStatusVariant(batch.status)" class="ml-2">
                                            {{ getBatchStatusText(batch.status) }}
                                        </b-badge>
                                    </h5>
                                </b-col>
                                <b-col cols="auto">
                                    <div class="d-flex" style="gap: 8px;">
                                        <!-- Resume button for in-progress batches -->
                                        <b-button
                                            v-if="batch.status === 0"
                                            variant="success"
                                            size="sm"
                                            @click="resumeBatch(batch)">
                                            <i class="fas fa-play" /> Resume Batch
                                        </b-button>

                                        <!-- Abandon button for in-progress batches -->
                                        <b-button
                                            v-if="batch.status === 0"
                                            variant="danger"
                                            size="sm"
                                            @click="confirmAbandonBatch(batch)">
                                            <i class="fas fa-times-circle" /> Abandon
                                        </b-button>

                                        <!-- Reprint button for completed batches -->
                                        <b-button
                                            v-if="batch.status === 1 && batch.packageSize"
                                            variant="primary"
                                            size="sm"
                                            @click="reprintLabels(batch)"
                                            :disabled="!hasSupportedClientService || printingBatchId === batch.id">
                                            <i class="fas fa-print" />
                                            <span v-if="printingBatchId === batch.id">
                                                <b-spinner small class="mr-1" /> Printing...
                                            </span>
                                            <span v-else>
                                                Reprint Labels
                                            </span>
                                        </b-button>
                                    </div>
                                </b-col>
                            </b-row>
                        </b-card-title>

                        <!-- Batch Information -->
                        <b-row class="mb-3">
                            <b-col cols="6">
                                <p class="mb-1"><strong>Compound:</strong> {{ batch.compound?.name || compound?.name }}</p>
                                <p class="mb-1"><strong>Lot Number:</strong> {{ batch.lotNumber || 'N/A' }}</p>
                                <p class="mb-1"><strong>Batch Size:</strong> {{ batch.batchSize }} {{ batch.compound?.unitOfMeasure || compound?.unitOfMeasure }}</p>
                                <p class="mb-1">
                                    <strong>Package Size:</strong> {{ batch.packageSize ? `${batch.packageSize} ${batch.compound?.unitOfMeasure || compound?.unitOfMeasure}` : 'N/A' }}
                                    <b-badge v-if="batch.status === 1 && !batch.packageSize" variant="warning" class="ml-2">
                                        <i class="fas fa-exclamation-triangle" /> Cannot reprint labels without package size
                                    </b-badge>
                                </p>
                            </b-col>
                            <b-col cols="6">
                                <p class="mb-1"><strong>Created:</strong> {{ formatDate(batch.created) }}</p>
                                <p class="mb-1"><strong>Created By:</strong> {{ batch.createdBy }}</p>
                                <p class="mb-1"><strong>Beyond Use Date:</strong> {{ batch.beyondUseDate ? formatDate(batch.beyondUseDate) : 'N/A' }}</p>
                                <p class="mb-1" v-if="batch.rxNumber">
                                    <strong>Rx Number:</strong> {{ batch.rxNumber }}-{{ batch.rfNumber }}
                                    <b-badge variant="info" class="ml-2">
                                        <i class="fas fa-prescription" /> Prescription Batch
                                    </b-badge>
                                </p>
                            </b-col>
                        </b-row>

                        <!-- Preparation Steps History -->
                        <div>
                            <h6 class="mb-3 cursor-pointer" @click="toggleSteps(batch.id)">
                                <i :class="{ 'fas fa-chevron-right': true, 'fa-rotate-90': expandedSteps[batch.id] }" style="transition: .2s all" />
                                <i class="fas fa-clipboard-list" /> Preparation Steps
                                <small class="text-muted ml-2">
                                    ({{ batch.steps?.length || 0 }} {{ (batch.steps?.length || 0) === 1 ? 'step' : 'steps' }})
                                </small>
                            </h6>

                            <b-collapse :visible="expandedSteps[batch.id]">
                                <div v-if="!batch.steps || batch.steps.length === 0">
                                    <p class="text-muted">No preparation steps recorded for this batch.</p>
                                </div>

                                <div v-else>
                                    <div v-for="(step, index) in batch.steps" :key="step.id" class="mb-3">
                                        <b-card :border-variant="step.completed ? 'success' : 'secondary'" body-class="p-3">
                                            <div class="d-flex align-items-start">
                                                <div class="mr-3">
                                                    <b-badge variant="primary" pill class="p-2">
                                                        Step {{ step.stepNumber }}
                                                    </b-badge>
                                                </div>
                                                <div class="flex-grow-1">
                                                    <h6>{{ step.description }}</h6>

                                                    <!-- Weight Information -->
                                                    <div v-if="step.expectedWeight || step.actualWeight" class="mt-2">
                                                        <b-row>
                                                            <b-col cols="4">
                                                                <small class="text-muted">Expected Weight:</small><br />
                                                                <strong v-if="step.expectedWeight">{{ step.expectedWeight.toFixed(2) }} {{ batch.compound?.unitOfMeasure || compound?.unitOfMeasure }}</strong>
                                                                <span v-else class="text-muted">N/A</span>
                                                            </b-col>
                                                            <b-col cols="4">
                                                                <small class="text-muted">Actual Weight:</small><br />
                                                                <strong v-if="step.actualWeight">{{ step.actualWeight.toFixed(2) }} {{ batch.compound?.unitOfMeasure || compound?.unitOfMeasure }}</strong>
                                                                <span v-else class="text-muted">Not recorded</span>
                                                            </b-col>
                                                            <b-col cols="4">
                                                                <small class="text-muted">Tolerance Status:</small><br />
                                                                <b-badge v-if="step.actualWeight != null && step.withinTolerance !== null"
                                                                    :variant="step.withinTolerance ? 'success' : 'danger'">
                                                                    {{ step.withinTolerance ? '✓ Within Tolerance' : '✗ Out of Tolerance' }}
                                                                </b-badge>
                                                                <span v-else class="text-muted">N/A</span>
                                                            </b-col>
                                                        </b-row>
                                                    </div>

                                                    <!-- Photo -->
                                                    <div v-if="step.requirePicture" class="mt-3">
                                                        <small class="text-muted">Photo Documentation:</small><br />
                                                        <div v-if="step.photoId">
                                                            <img v-if="shouldLoadImage(step.photoId)"
                                                                :src="getImageUrl(step.photoId)"
                                                                alt="Step Photo"
                                                                class="img-thumbnail mt-2"
                                                                style="max-width: 400px; cursor: pointer;"
                                                                @click="showImageModal(step.photoId)"
                                                                @error="onImageError(step.photoId)" />
                                                            <div v-else class="mt-2">
                                                                <b-button size="sm" variant="outline-primary" @click="loadImage(step.photoId)">
                                                                    <i class="fas fa-image" /> Load Photo
                                                                </b-button>
                                                            </div>
                                                        </div>
                                                        <div v-else class="mt-2">
                                                            <b-badge variant="warning">
                                                                <i class="fas fa-exclamation-triangle" /> Photo required but not captured
                                                            </b-badge>
                                                        </div>
                                                    </div>

                                                    <!-- Completion Information -->
                                                    <div v-if="step.completed" class="mt-2 pt-2 border-top">
                                                        <small class="text-muted">
                                                            <i class="fas fa-check-circle text-success" />
                                                            Completed on {{ formatDateTime(step.completed) }} by {{ step.completedBy }}
                                                        </small>
                                                    </div>
                                                    <div v-else class="mt-2 pt-2 border-top">
                                                        <small class="text-warning">
                                                            <i class="fas fa-clock" /> Not yet completed
                                                        </small>
                                                    </div>
                                                </div>
                                            </div>
                                        </b-card>
                                    </div>
                                </div>
                            </b-collapse>
                        </div>

                        <!-- Ingredients Used -->
                        <div v-if="batch.ingredients && batch.ingredients.length > 0" class="mt-4">
                            <h6 class="mb-3 cursor-pointer" @click="toggleIngredients(batch.id)">
                                <i :class="{ 'fas fa-chevron-right': true, 'fa-rotate-90': expandedIngredients[batch.id] }" style="transition: .2s all" />
                                <i class="fas fa-flask" /> Ingredients Used
                                <small class="text-muted ml-2">
                                    ({{ batch.ingredients.length }} {{ batch.ingredients.length === 1 ? 'ingredient' : 'ingredients' }})
                                </small>
                            </h6>
                            <b-collapse :visible="expandedIngredients[batch.id]">
                                <b-table-simple hover small bordered>
                                    <b-thead>
                                        <b-tr>
                                            <b-th>Ingredient</b-th>
                                            <b-th>Quantity</b-th>
                                            <b-th>Lot Number</b-th>
                                            <b-th>Expiration Date</b-th>
                                        </b-tr>
                                    </b-thead>
                                    <b-tbody>
                                        <b-tr v-for="ingredient in batch.ingredients" :key="ingredient.id">
                                            <b-td>{{ ingredient.drugName || 'Unknown' }}</b-td>
                                            <b-td>
                                                {{ ingredient.quantity.toFixed(2) }} {{ ingredient.unitOfMeasure }}
                                            </b-td>
                                            <b-td>
                                                <span v-if="ingredient.lotNumber">{{ ingredient.lotNumber }}</span>
                                                <span v-else class="text-muted">Not recorded</span>
                                            </b-td>
                                            <b-td>
                                                <span v-if="ingredient.expirationDate">{{ formatDate(ingredient.expirationDate) }}</span>
                                                <span v-else class="text-muted">Not recorded</span>
                                            </b-td>
                                        </b-tr>
                                    </b-tbody>
                                </b-table-simple>
                            </b-collapse>
                        </div>
                    </b-card>
                </div>
            </div>

            <!-- No Results -->
            <div v-if="searched && batches.length > 0 && filteredBatches.length === 0">
                <b-alert variant="info" show>
                    <i class="fas fa-filter" /> No batches match the current filters. Try adjusting your filter criteria.
                </b-alert>
            </div>
            <div v-if="searched && batches.length === 0">
                <b-alert variant="warning" show>
                    <i class="fas fa-exclamation-triangle" /> No batches found.
                </b-alert>
            </div>
        </div>

        <!-- Image Modal -->
        <b-modal v-model="showImageModalFlag" title="Step Photo" size="xl" hide-footer>
            <img v-if="selectedImageId" :src="getImageUrl(selectedImageId)" alt="Step Photo" class="img-fluid" />
        </b-modal>
    </card>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { CompoundBatch } from '@/models/Drug/CompoundDrug';
import { CompoundDrug } from '@/models/Drug/CompoundDrug';
import Axios from 'axios';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import { compareVersions } from "compare-versions";

const minClientVersion = "1.3.0";

@Component({
    name: "CompoundBatchAuditPage"
})
export default class CompoundBatchAuditPage extends Vue {
    @Prop() private compoundId?: string;

    private searchTerm = '';
    private batches: CompoundBatch[] = [];
    private compound: CompoundDrug | null = null;
    private searched = false;
    private showImageModalFlag = false;
    private selectedImageId: number | null = null;
    private loadedImages: number[] = [];
    private imageUrls: { [key: number]: string } = {};
    private expandedSteps: { [key: number]: boolean } = {};
    private expandedIngredients: { [key: number]: boolean } = {};

    // Printer management properties
    protected isLoadingPrinters = false;
    protected localPrinters: any[] = [];
    private activeLocalPrinter: any | null = null;
    protected hasSupportedClientService: boolean | null = null;
    protected labelZpl: any = {};
    protected printingBatchId: number | null = null;

    // Filter properties
    private statusFilter: string = 'completed';
    private startDate: string = '';
    private endDate: string = '';

    private statusOptions = [
        { value: 'all', text: 'All Batches' },
        { value: 'completed', text: 'Completed Only' },
        { value: 'in-progress', text: 'In Progress Only' },
        { value: 'abandoned', text: 'Abandoned Only' }
    ];

    get pageTitle(): string {
        if (this.compoundId && this.compound) {
            return `Batch History - ${this.compound.name}`;
        }
        return 'Compound Batch Audit';
    }

    get filteredBatches(): CompoundBatch[] {
        let filtered = [...this.batches];

        // Filter by status
        if (this.statusFilter === 'completed') {
            filtered = filtered.filter(b => b.status === 1);
        } else if (this.statusFilter === 'in-progress') {
            filtered = filtered.filter(b => b.status === 0);
        } else if (this.statusFilter === 'abandoned') {
            filtered = filtered.filter(b => b.status === 2);
        }

        // Filter by date range (comparing only date parts, not time)
        if (this.startDate) {
            const startDateOnly = this.getDateOnly(this.startDate);
            filtered = filtered.filter(b => {
                const createdDateOnly = this.getDateOnly(b.created);
                return createdDateOnly >= startDateOnly;
            });
        }

        if (this.endDate) {
            const endDateOnly = this.getDateOnly(this.endDate);
            filtered = filtered.filter(b => {
                const createdDateOnly = this.getDateOnly(b.created);
                return createdDateOnly <= endDateOnly;
            });
        }

        return filtered;
    }

    async mounted() {
        // Set default date range (1 month ago to today)
        this.initializeDateRange();

        if (this.compoundId) {
            await this.loadBatchesByCompound();
        }

        // Initialize printer support
        this.getLocalPrinters();
        this.getSupportedClientService();
    }

    beforeDestroy() {
        // Clean up object URLs to prevent memory leaks
        Object.values(this.imageUrls).forEach(url => {
            URL.revokeObjectURL(url);
        });
    }

    initializeDateRange() {
        const today = new Date();
        const tomorrow = new Date();
        tomorrow.setDate(today.getDate() + 1);
        const oneMonthAgo = new Date();
        oneMonthAgo.setMonth(today.getMonth() - 1);

        // Format dates as YYYY-MM-DD for input[type="date"]
        this.endDate = this.formatDateForInput(tomorrow);
        this.startDate = this.formatDateForInput(oneMonthAgo);
    }

    formatDateForInput(date: Date): string {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    getDateOnly(dateValue: string | Date | null): string {
        if (!dateValue) return '';
        const date = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
        return this.formatDateForInput(date);
    }

    resetFilters() {
        this.statusFilter = 'completed';
        this.initializeDateRange();
    }

    async loadBatchesByCompound() {
        if (!this.compoundId) return;

        this.searched = true;
        this.batches = [];

        try {
            // Load compound details
            const compoundResponse = await Axios.get<CompoundDrug>(`/Compound/${this.compoundId}`);
            this.compound = compoundResponse.data;

            // Load all batches for this compound
            const batchesResponse = await Axios.get<CompoundBatch[]>(`/CompoundBatch/compound/${this.compoundId}`);
            this.batches = batchesResponse.data;
        } catch (error) {
            console.error('Error loading batches for compound:', error);
            this.$notification(NotificationOptions.error(error));
        }
    }

    async searchBatch() {
        if (!this.searchTerm.trim()) return;

        this.searched = true;
        this.batches = [];
        this.compound = null;

        try {
            // Try searching by batch number or lot number
            const response = await Axios.get<CompoundBatch>(`/CompoundBatch/number/${this.searchTerm.trim()}`);
            this.batches = [response.data];

            // Load compound details
            if (response.data?.compoundID) {
                const compoundResponse = await Axios.get<CompoundDrug>(`/Compound/${response.data.compoundID}`);
                this.compound = compoundResponse.data;
            }
        } catch (error) {
            console.error('Error searching for batch:', error);
            if ((error as any)?.response?.status === 404) {
                // Not found, show no results message
                this.$notification(NotificationOptions.warning('No batch found with that batch number or lot number.'));
            } else {
                this.$notification(NotificationOptions.error(error));
            }
        }
    }

    formatDate(dateString: string | Date | null): string {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString();
    }

    formatDateTime(dateString: string | Date | null): string {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString();
    }

    showImageModal(photoId: number) {
        this.selectedImageId = photoId;
        this.showImageModalFlag = true;
        // Ensure the modal image is loaded
        this.loadImage(photoId);
    }

    shouldLoadImage(photoId: number): boolean {
        return this.loadedImages.includes(photoId);
    }

    async loadImage(photoId: number) {
        if (this.loadedImages.includes(photoId)) {
            return; // Already loaded
        }

        try {
            // Fetch the image as a blob using authenticated axios
            const response = await Axios.get(`/Image/${photoId}`, {
                responseType: 'blob'
            });

            // Create an object URL from the blob
            const imageUrl = URL.createObjectURL(response.data);

            // Store the URL and mark as loaded
            this.$set(this.imageUrls, photoId, imageUrl);
            this.loadedImages.push(photoId);
        } catch (error) {
            console.error(`Failed to load image ${photoId}:`, error);
            this.$notification(NotificationOptions.error(`Failed to load image ${photoId}`));
        }
    }

    onImageError(photoId: number) {
        console.error(`Failed to load image with ID: ${photoId}`);
        this.$notification(NotificationOptions.error(`Failed to load image ${photoId}`));
    }

    getImageUrl(photoId: number): string {
        // Return the blob URL if it's been loaded
        return this.imageUrls[photoId] || '';
    }

    async reprintLabels(batch: CompoundBatch) {
        if (!batch.packageSize || !batch.batchSize) {
            this.$notification(NotificationOptions.error("Cannot print labels: Package size or batch size is missing"));
            return;
        }

        this.printingBatchId = batch.id;

        try {
            // Calculate label count
            const labelCount = Math.ceil(batch.batchSize / batch.packageSize);

            // Get ZPL data from the API
            const zplRequest = {
                storeId: batch.storeId,
                labelCount: labelCount
            };
            const zplResponse = await Axios.post(`/CompoundBatch/${batch.id}/label-zpl`, zplRequest);
            this.labelZpl = zplResponse.data;

            // Determine if this is a prescription batch (has rxNumber)
            const isPrescriptionBatch = !!batch.rxNumber;

            // Only apply numbering for prescription batches with multiple labels
            const applyNumbering = isPrescriptionBatch && labelCount > 1;

            // Print labels using client service
            await this.sendLabelZplToClientService(batch, labelCount, applyNumbering);
            this.$notification(NotificationOptions.success(`${labelCount} label(s) sent to printer successfully!`));
        } catch (error) {
            console.error('Error reprinting batch labels', error);
            this.$notification(NotificationOptions.error('Failed to print labels. Please try again.'));
        } finally {
            this.printingBatchId = null;
        }
    }

    async sendLabelZplToClientService(batch: CompoundBatch, labelCount: number, applyNumbering: boolean = false): Promise<boolean> {
        if (!this.labelZpl) {
            this.$notification(NotificationOptions.error("No label data to print"));
            return false;
        }

        try {
            // Try to use WebSocket connection via shared worker if available
            const workerPort = (this as any).$workerPort;
            if (workerPort) {
                // Print warnings via WebSocket
                if (this.labelZpl.warnings?.length) {
                    const warningsRequest = this.buildPrinterRequest(
                        this.labelZpl.warnings.join(""),
                        `${batch.batchNumber}-warnings`
                    );
                    workerPort.postMessage(["clientServicePrintRaw", warningsRequest]);
                    await new Promise(resolve => setTimeout(resolve, 100));
                }

                // Print labels via WebSocket
                for (let i = 1; i <= labelCount; i++) {
                    // Only apply numbering if requested (for prescriptions with multiple labels)
                    const modifiedZpl = applyNumbering
                        ? this.processLabelNumbering(this.labelZpl.primary, i, labelCount)
                        : this.labelZpl.primary;
                    const labelRequest = this.buildPrinterRequest(
                        modifiedZpl,
                        `${batch.batchNumber}-label-${i}`
                    );
                    workerPort.postMessage(["clientServicePrintRaw", labelRequest]);
                    await new Promise(resolve => setTimeout(resolve, 100));
                }

                return true;
            }

            // Fallback to HTTP if WebSocket not available
            const printerEndpoint = "http://localhost:7274/print/raw";

            if (this.labelZpl.warnings?.length) {
                const warningsRequest = this.buildPrinterRequest(
                    this.labelZpl.warnings.join(""),
                    `${batch.batchNumber}-warnings`
                );
                await Axios.post(printerEndpoint, warningsRequest);
            }

            for (let i = 1; i <= labelCount; i++) {
                // Only apply numbering if requested (for prescriptions with multiple labels)
                const modifiedZpl = applyNumbering
                    ? this.processLabelNumbering(this.labelZpl.primary, i, labelCount)
                    : this.labelZpl.primary;
                const labelRequest = this.buildPrinterRequest(
                    modifiedZpl,
                    `${batch.batchNumber}-label-${i}`
                );
                await Axios.post(printerEndpoint, labelRequest);
            }

            return true;
        } catch (err) {
            console.error(err);
            this.$notification(NotificationOptions.error(err));
            return false;
        }
    }

    buildPrinterRequest(rawContent: string, documentName: string) {
        return {
            printerName: this.activeLocalPrinter?.name,
            rawContent: rawContent,
            documentName: documentName,
            copies: 1
        };
    }

    processLabelNumbering(zplContent: string, currentLabel: number, totalLabels: number): string {
        if (totalLabels > 1) {
            return zplContent.replace('{{LabelCount}}', `${currentLabel} of ${totalLabels}`);
        } else {
            return zplContent.replace('{{LabelCount}}', '');
        }
    }

    async getSupportedClientService() {
        try {
            // Try to use WebSocket connection via shared worker if available
            const workerPort = (this as any).$workerPort;
            if (workerPort) {
                // Request version through the shared worker
                const version = await new Promise<string | null>((resolve) => {
                    const timeout = setTimeout(() => {
                        console.log("WebSocket version request timed out, falling back to HTTP");
                        resolve(null);
                    }, 3000);

                    const handleMessage = (e: MessageEvent) => {
                        if (e.data[0] === "clientServiceVersion") {
                            clearTimeout(timeout);
                            workerPort.removeEventListener("message", handleMessage);
                            resolve(e.data[1] || null);
                        }
                    };

                    workerPort.addEventListener("message", handleMessage);
                    // The version should already be cached, but we can request a refresh
                    workerPort.postMessage(["clientServiceRefreshVersion", null]);
                });

                if (version) {
                    this.hasSupportedClientService = compareVersions(version, minClientVersion) >= 0;
                    return;
                }
            }

            // Fallback to HTTP if WebSocket not available or failed
            const response = await this.$http.get("http://localhost:7274/version");
            const version = response.data;

            this.hasSupportedClientService = compareVersions(version, minClientVersion) >= 0;
        } catch {
            this.hasSupportedClientService = false;
        }
    }

    async getLocalPrinters() {
        try {
            this.isLoadingPrinters = true;

            // Try to use WebSocket connection via shared worker if available
            const workerPort = (this as any).$workerPort;
            if (workerPort) {
                // Request printers through the shared worker
                const printers = await new Promise<any[]>((resolve) => {
                    const timeout = setTimeout(() => {
                        console.log("WebSocket printer request timed out, falling back to HTTP");
                        resolve([]);
                    }, 3000);

                    const handleMessage = (e: MessageEvent) => {
                        if (e.data[0] === "clientServicePrinters") {
                            clearTimeout(timeout);
                            workerPort.removeEventListener("message", handleMessage);
                            resolve(e.data[1] || []);
                        }
                    };

                    workerPort.addEventListener("message", handleMessage);
                    workerPort.postMessage(["clientServiceRefreshPrinters", null]);
                });

                if (printers.length > 0) {
                    this.localPrinters = printers;

                    this.localPrinters.sort((a, b) => {
                        const aHasZPL = a.name.toLowerCase().includes('zpl') || a.name.toLowerCase().includes('zebra');
                        const bHasZPL = b.name.toLowerCase().includes('zpl') || b.name.toLowerCase().includes('zebra');

                        if (aHasZPL && !bHasZPL) return -1;
                        if (!aHasZPL && bHasZPL) return 1;

                        return a.name.localeCompare(b.name);
                    });

                    this.handleDefaultPrinter();
                    return;
                }
            }

            // Fallback to HTTP if WebSocket not available or no printers received
            const response = await this.$http.get("http://localhost:7274/print/devices");
            this.localPrinters = response.data;

            this.localPrinters.sort((a, b) => {
                const aHasZPL = a.name.toLowerCase().includes('zpl') || a.name.toLowerCase().includes('zebra');
                const bHasZPL = b.name.toLowerCase().includes('zpl') || b.name.toLowerCase().includes('zebra');

                if (aHasZPL && !bHasZPL) return -1;
                if (!aHasZPL && bHasZPL) return 1;

                return a.name.localeCompare(b.name);
            });

            this.handleDefaultPrinter();
        } catch {
            //ignore
        } finally {
            this.isLoadingPrinters = false;
        }
    }

    handleDefaultPrinter() {
        const lastPrinter = localStorage.getItem("active-compound-batch-printer");
        if (lastPrinter) {
            this.activeLocalPrinter = this.localPrinters?.find(p => p.name === lastPrinter);
            return;
        }

        if (this.localPrinters.length) {
            this.activeLocalPrinter = this.localPrinters[0];
        }
    }

    @Watch('activeLocalPrinter')
    activeLocalPrinterChanged() {
        localStorage.setItem("active-compound-batch-printer", this.activeLocalPrinter?.name);
    }

    get hasLocalPrinters() {
        return !!this.localPrinters.length;
    }

    prettyPrinterName(printer: any) {
        if (!printer) return "No Printer Defined";
        if (!printer.serverName) return printer.name;
        return printer.name.replace(printer.serverName, "").replace(/\\/g, "");
    }

    toggleSteps(batchId: number) {
        this.$set(this.expandedSteps, batchId, !this.expandedSteps[batchId]);
    }

    toggleIngredients(batchId: number) {
        this.$set(this.expandedIngredients, batchId, !this.expandedIngredients[batchId]);
    }

    getBatchStatusText(status: number): string {
        switch (status) {
            case 0:
                return 'In Progress';
            case 1:
                return 'Completed';
            case 2:
                return 'Abandoned';
            default:
                return 'Unknown';
        }
    }

    getBatchStatusVariant(status: number): string {
        switch (status) {
            case 0:
                return 'warning';
            case 1:
                return 'success';
            case 2:
                return 'danger';
            default:
                return 'secondary';
        }
    }

    resumeBatch(batch: CompoundBatch) {
        // Determine mode based on whether it's a prescription batch
        const mode = batch.rxNumber ? 'prescription' : 'batch';

        // Navigate to CompoundFillingPage with batch ID
        this.$router.push({
            name: 'CompoundFillingPage',
            params: {
                compoundId: batch.compoundID.toString(),
                mode: mode
            },
            query: {
                batchId: batch.id.toString()
            }
        });
    }

    confirmAbandonBatch(batch: CompoundBatch) {
        this.$bvModal.msgBoxConfirm(
            `Are you sure you want to abandon batch ${batch.batchNumber}? This action cannot be undone.`,
            {
                title: 'Confirm Abandon Batch',
                size: 'md',
                buttonSize: 'sm',
                okVariant: 'danger',
                okTitle: 'Yes, Abandon Batch',
                cancelTitle: 'Cancel',
                footerClass: 'p-2',
                hideHeaderClose: false,
                centered: true
            }
        )
            .then(async (confirmed) => {
                if (confirmed) {
                    await this.abandonBatch(batch);
                }
            })
            .catch(err => {
                console.error("Error in abandon batch confirmation:", err);
            });
    }

    async abandonBatch(batch: CompoundBatch) {
        try {
            await Axios.post(`/CompoundBatch/${batch.id}/abandon`);
            this.$notification(NotificationOptions.success(`Batch ${batch.batchNumber} has been abandoned.`));

            // Reload the batch data to reflect the status change
            if (this.compoundId) {
                await this.loadBatchesByCompound();
            } else {
                await this.searchBatch();
            }
        } catch (error) {
            console.error('Error abandoning batch', error);
            this.$notification(NotificationOptions.error('Failed to abandon batch. Please try again.'));
        }
    }
}
</script>

<style scoped>
.border-top {
    border-top: 1px solid #dee2e6;
}

.cursor-pointer {
    cursor: pointer;
    user-select: none;
}

.cursor-pointer:hover {
    color: #007bff;
}
</style>
